import React from 'react';
import PropTypes from 'prop-types';

import { Ad } from '@autovia-uk/polaris-components/components/atoms/Ad';
import { Loading } from '@autovia-uk/polaris-components/components/atoms/Loading';
import { Search } from '@autovia-uk/polaris-components/components/molecules/Search';
import { getLoadMore } from 'SharedPartials/getLoadMore';
import {
  getBreadcrumbs,
  getSearchPageItems,
} from 'SharedPartialsLocal';
import { getRightSideAdConfig } from 'Helpers';
import { getFilters } from './filters';
import { getBlankMessage } from './functions';

// Template styles and imports
import 'Styles/components/templates/_Search.scss';

const SearchTemplate = (props) => {
  const {
    layoutData: {
      page: {
        breadcrumbs,
        query: {
          keywords = '',
          filter = 'ALL',
        },
      },
    },
    pagination: {
      setPageRef,
      pages,
      isLoading: isLoadingProp,
      loadMore,
      hasMore,
      nextUrl,
    },
  } = props;

  const filters = getFilters();
  const filterIds = filters.map(({ id }) => id);
  let searchTimeout = null;

  /**
   * Search function
   *
   * @param {*} keywordsSearch
   * @param {*} filterSearch
   */
  const doSearch = (keywordsSearch, filterSearch) => {
    let url = '/search';

    if (keywordsSearch) {
      url += `/${encodeURIComponent(keywordsSearch)}`;
    }
    if (filterSearch && filterSearch !== 'ALL' && filterIds.includes(filterSearch)) {
      if (!keywordsSearch) {
        url += '/'; // Add extra slash for an empty search query.
      }
      url += `/filter/${filterSearch.toLowerCase()}`;
    }
    url += '/';

    window.location.href = url;
  };

  /**
   * Debounce search
   *
   * @param {*} keywordsSearch
   * @param {*} filterSearch
   * @param debounce
   */
  const debounceSearch = (keywordsSearch, filterSearch, debounce = true) => {
    if (debounce) {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(doSearch, 1200, keywordsSearch, filterSearch);
    } else {
      doSearch(keywordsSearch, filterSearch);
    }
  };

  const isSearching = !Object.keys(pages).length && isLoadingProp;
  const isLoading = Object.keys(pages).length > 0 && isLoadingProp;
  const showFilters = true;
  const resultPages = Object.entries(pages).length > 0 ? pages : { 0: [] }; // always match initial pages structure from withPagination
  const loadMoreParams = {
    label: isLoading ? 'Loading...' : 'Load More',
    loadMore,
    isLoading,
    extraClassNames: {
      'polaris__load-more--small': true,
    },
    href: nextUrl,
  };

  return (
    <>
      <div className="polaris__body -search-template">
        <main className="polaris__main" id="main">
          <div className="polaris__core-content polaris__content">
            {getBreadcrumbs(breadcrumbs, false, '', 'Search Results')}
          </div>
          <div className="polaris__content--with-bg">
            <div className="polaris__content">
              <Search
                label=""
                placeholder="Search..."
                url="/search"
                searchTerm={keywords}
                filtersLabel="Filter by"
                currentFilter={filter || 'ALL'}
                onSubmit={debounceSearch}
                filters={filters}
                showFilters={showFilters}
                inputId="polaris__search--template-input"
                extraClassNames={{ 'polaris__search--results': true }}
              />
            </div>
          </div>
          <div className="polaris__core-content polaris__content polaris__core-content-search polaris__simple-grid -search-template">
            <div className="polaris__simple-grid--main">
              {!keywords && (
                <p>
                  Please enter a search term in the search box above.
                </p>
              )}
              <div data-previous="1" ref={setPageRef} />
              {isSearching && (
                <div className="loading-center">
                  <Loading />
                  <p>Loading search results</p>
                </div>
              )}
              {!isSearching && keywords && getSearchPageItems(resultPages, setPageRef, keywords, getBlankMessage(keywords))}
              {!isSearching && hasMore && getLoadMore(loadMoreParams)}
            </div>
            <div className="polaris__simple-grid--aside">
              <Ad {...getRightSideAdConfig()} />
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

SearchTemplate.propTypes = {
  layoutData: PropTypes.shape({
    page: PropTypes.object.isRequired,
  }).isRequired,
  pagination: PropTypes.shape({
    isLoading: PropTypes.bool,
    loadMore: PropTypes.func,
    page: PropTypes.number,
    pages: PropTypes.object,
    setPageRef: PropTypes.func,
    hasMore: PropTypes.bool,
    nextUrl: PropTypes.string,
  }).isRequired,
  config: PropTypes.shape({
    globalSettings: PropTypes.shape({
      promoBoxSettings: PropTypes.object.isRequired,
    }).isRequired,
  }).isRequired,
};

export default SearchTemplate;
